import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Tooltip, Typography, useTheme } from '@mui/material';
import { EmailIcon, PhoneIcon } from '@sharedCR/coreDesignSystem/icons';
import { LOANS_ROUTING_PARAMS, LoansPaths } from 'app/configs/routesConfig/loans';
import { checkIsApplicationAtLeadStage } from 'app/pages/loanApplication/LoanApplication.utils';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { FeatureFlag } from 'constants/featureFlags';
import { LeadSubtabRouteParam, LoanApplicationTabRouteParam } from 'constants/loanApplication';
import useCompanyId from 'hooks/useCompanyId';

import {
  extensionMakeCallAction,
  makeCallWithDialogAction,
  setActiveTabWithNavigationAction,
} from 'store/loanApplications/actions';
import { selectDealFromApplication } from 'store/loanApplications/selectors';

import { getStyles } from '../Activities.styles';

// TODO: add dependance from telephony
const isPhoneActive = false;

const ContactItem = ({ fullName = '', emailAddress = '', phoneNumber = '', onClickEmail }) => {
  const theme = useTheme();
  const classes = getStyles(theme);
  const companyId = useCompanyId();
  const { applicationId } = useParams();
  const dispatch = useDispatch();

  const isCallConfirmationEnabled = useFeatureToggle(FeatureFlag.EnableCallConfirmation);

  const deal = useSelector(selectDealFromApplication);

  const isLeadStage = !!applicationId && checkIsApplicationAtLeadStage(deal?.applicationStatus);
  const fullNameTabToNavigate = isLeadStage
    ? LoanApplicationTabRouteParam.LEAD
    : LoanApplicationTabRouteParam.BORROWERS;
  const subtabToNavigate = isLeadStage ? LeadSubtabRouteParam.LEAD_INFO : null;

  const onPhoneNumberClick = useCallback(() => {
    if (isPhoneActive) {
      if (isCallConfirmationEnabled) {
        dispatch(makeCallWithDialogAction({ applicationId, fullName, companyId, phoneNumber }));

        return;
      }

      dispatch(
        extensionMakeCallAction({
          applicationId,
          fullName,
          companyId,
          phoneNumber,
        }),
      );
    }
  }, [isCallConfirmationEnabled, dispatch, applicationId, fullName, companyId, phoneNumber]);

  const onEmailClick = useCallback(() => onClickEmail(emailAddress), [onClickEmail, emailAddress]);

  const onNameClick = useCallback(() => {
    dispatch(
      setActiveTabWithNavigationAction({
        activeTab: fullNameTabToNavigate,
        activeSubtab: subtabToNavigate,
        applicationId,
      }),
    );
  }, [dispatch, applicationId, fullNameTabToNavigate, subtabToNavigate]);

  return (
    <div className="flex flex-col py-5">
      <Typography
        className={clsx('py-1 truncate', {
          'cursor-pointer': fullName,
        })}
        sx={fullName ? classes.textColor : undefined}
        variant="bold14"
        onClick={fullName ? onNameClick : null}
        {...(fullName && {
          onClick: undefined,
          role: 'button',
          component: Link,
          to: LoansPaths.applicationManagement.generatePath({
            [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
            [LOANS_ROUTING_PARAMS.tab]: fullNameTabToNavigate,
            [LOANS_ROUTING_PARAMS.subtab]: subtabToNavigate,
          }),
        })}
      >
        {fullName || 'N/A'}
      </Typography>

      {!!emailAddress && (
        <Tooltip title={emailAddress}>
          <div className="inline-flex cursor-pointer" onClick={onEmailClick}>
            <EmailIcon className="mr-1 my-auto" size={IconSize[16]} />
            <Typography className="py-1 truncate" sx={classes.textColor} variant="regular14">
              {emailAddress}
            </Typography>
          </div>
        </Tooltip>
      )}
      {!emailAddress && (
        <Typography className="py-1 truncate" variant="regular14">
          N/A
        </Typography>
      )}

      <div
        className={clsx('inline-flex', { 'cursor-pointer': isPhoneActive && phoneNumber })}
        onClick={phoneNumber && isPhoneActive ? onPhoneNumberClick : undefined}
      >
        {!!phoneNumber && <PhoneIcon className="mr-1 my-auto" size={IconSize[16]} />}
        <Typography
          className="py-1 truncate"
          sx={phoneNumber && isPhoneActive ? classes.textColor : undefined}
          variant="regular14"
        >
          {phoneNumber || 'N/A'}
        </Typography>
      </div>
    </div>
  );
};

ContactItem.propTypes = {
  fullName: PropTypes.string,
  emailAddress: PropTypes.string,
  phoneNumber: PropTypes.string,
  onClickEmail: PropTypes.func,
};

export default memo(ContactItem);
